import styled from '@emotion/styled';
import { AddOns } from '@racemap/sdk/schema/billing/AddOns';
import { StripeProducts } from '@racemap/sdk/schema/billing/StripeProducts';
import { RacemapColors } from '@racemap/utilities/consts/common';
import {
  AddOnDocUrls,
  AddOnLabels,
  ModulesStates,
  RentGPSTrackerFormUrl,
  RentGPSTrackerInfoUrl,
} from '@racemap/utilities/consts/events';
import { isChildUserAccount } from '@racemap/utilities/functions/utils';
import type { User_Legacy } from '@racemap/utilities/types/users';
import { Button, Flex, Modal, Typography } from 'antd';
import type { Immutable } from 'immer';
import type { FC, ReactElement, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Placeholder } from 'src/components/BasicComponents/Placeholder';
import type { Price } from 'src/components/CostCalculator/getProductPriceFactory';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import { useCurrentUser } from 'src/lib/customHooks/useCurrentUser';
import { useGetProductPrice } from 'src/lib/customHooks/useGetProductPrice';
import { formatPrice } from 'src/misc/formatPrice';
import type { CurrentEvent } from 'src/store/events/events_reducers';
import { useUser } from '../../../../lib/customHooks';
import {
  IconDatafeed,
  IconDocs,
  IconGear,
  IconInfo,
  IconKey,
  IconLeaderboard,
  IconMobile,
  IconSponsor,
} from '../../../BasicComponents/Icons';

const { Text } = Typography;

interface Props {
  addOnKey: AddOns;
}

export const AddOnCard: FC<Props> = ({ addOnKey }) => {
  const event = useCurrentEvent();
  const currentUser = useCurrentUser();
  const { user: eventCreator } = useUser({ userId: event?.creatorId });
  const getPriceOfProduct = useGetProductPrice({ isReseller: eventCreator?.isReseller });
  const navigate = useNavigate();
  const addOnElement = addOnElements[addOnKey];

  if (event == null || eventCreator == null || addOnElement == null) return null;
  const { title, icon, subtitle, actions } = addOnElement;
  const option = event.modules[addOnKey as keyof typeof event.modules];
  const state = getModuleState(option);
  const defaultAction = actions.find((action) => action.actionIsDefault);

  const handleCardClick = () => {
    if (defaultAction == null) return;
    handleButtonAction(defaultAction);
  };

  const handleButtonAction = (action: (typeof actions)[0]) => {
    switch (action.actionType) {
      case 'link':
        if (action.link.startsWith('http')) {
          window.open(action.link, '_blank');
          return;
        }
        navigate(action.link);
        break;
      case 'modal':
        Modal.info({
          title: null,
          content: action.content,
          icon: null,
          width: 600,
          footer: null,
          closable: true,
        });
        break;
    }
  };

  const card = (
    <CardAddOn>
      <IconWrapper>{icon}</IconWrapper>
      <TitleAndSubtitle title={title} subtitle={subtitle} />
      <Flex gap={10} align="center" justify="end">
        <PriceOrStateFieldWrapper>
          {getPriceOrStateField(state, addOnKey, currentUser, getPriceOfProduct)}
        </PriceOrStateFieldWrapper>

        {actions.map((action) => (
          <Button
            key={action.title}
            icon={action.icon}
            type={action.actionIsDefault ? 'primary' : 'default'}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleButtonAction(action);
            }}
            style={{ width: 100 }}
          >
            {action.title}
          </Button>
        ))}
      </Flex>
    </CardAddOn>
  );

  if (defaultAction?.actionType === 'link') {
    return <Link to={defaultAction.link}>{card}</Link>;
  }

  return (
    <Container onClick={handleCardClick} onKeyUp={handleCardClick}>
      {card}
    </Container>
  );
};

const Container = styled.button`
  all: unset;
  cursor: pointer;
`;

const PriceOrStateFieldWrapper = styled.div`
  white-space: nowrap;
  `;

const TitleAndSubtitle: FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => {
  return (
    <Flex vertical className="title-and-subtitle">
      <Text strong>{title.toLocaleUpperCase()}</Text>
      <Text ellipsis type="secondary">
        {subtitle}
      </Text>
    </Flex>
  );
};

const getModuleState = (module: Immutable<CurrentEvent['modules'][ModuleKeys]>): ModulesStates => {
  if (module == null || typeof module !== 'object') return ModulesStates.NONE;
  if ('state' in module && module.state === ModulesStates.ACTIVATED) return ModulesStates.ACTIVATED;
  if ('enabled' in module && module.enabled) return ModulesStates.ACTIVATED;

  return ModulesStates.NONE;
};

type ModuleKeys = keyof CurrentEvent['modules'];

const addonProductMapping: Partial<Record<AddOns, StripeProducts>> = {
  [AddOns.API]: StripeProducts.API_ADDON,
  [AddOns.SPONSOR]: StripeProducts.SPONSOR_ADDON,
  [AddOns.TIMING]: StripeProducts.TIMING_ADDON,
  [AddOns.TRACKER_RENTAL]: StripeProducts.TRACKER_RENTAL,
};

const getPriceOrStateField = (
  state: ModulesStates,
  addOn: AddOns,
  currentUser: Immutable<User_Legacy> | null,
  getPriceOfProduct?: (product: StripeProducts, quantity: number, tier?: number | null) => Price,
): ReactNode => {
  if (currentUser == null) return null;
  if (state === ModulesStates.ACTIVATED) return <Text type="success">ON</Text>;
  if (addOn === AddOns.PREDICTIVE || addOn === AddOns.PARTICIPANTS)
    return <Text type="secondary">INCLUDED</Text>;
  if (isChildUserAccount(currentUser)) return <Text type="secondary">AVAILABLE</Text>;

  if (getPriceOfProduct == null) return <Placeholder width={100} />;

  if (addOn === AddOns.KEYS)
    return (
      <Text type="secondary">{`FROM €0 to ${formatPrice(getPriceOfProduct(StripeProducts.KEY, 1))}`}</Text>
    );

  if (addOn === AddOns.TRACKER_RENTAL)
    return (
      <Text type="secondary">
        {formatPrice(getPriceOfProduct(StripeProducts.TRACKER_RENTAL, 5))} p. 5 dev.
      </Text>
    );

  const product = addonProductMapping[addOn];
  if (product == null) return <Text type="danger">NOT AVAILABLE</Text>;
  return <Text type="secondary">{formatPrice(getPriceOfProduct(product, 1))}</Text>;
};

const addOnElements: Partial<
  Record<
    AddOns,
    {
      title: string;
      icon: ReactElement;
      subtitle: string;
      actions: Array<
        {
          icon: ReactElement;
          title: string;
          actionIsDefault: boolean;
        } & (
          | {
              actionType: 'link';
              link: string;
            }
          | {
              actionType: 'modal';
              content: ReactElement;
            }
        )
      >;
    }
  >
> = {
  [AddOns.API]: {
    title: AddOnLabels[AddOns.API],
    icon: <IconDatafeed size="2x" fixedWidth color={RacemapColors.PaleBlue} />,
    subtitle: 'For TV graphics, speed monitoring, live scoring',
    actions: [
      {
        icon: <IconGear />,
        title: 'Setup',
        actionType: 'link',
        link: '../dataapi',
        actionIsDefault: true,
      },
      {
        icon: <IconDocs />,
        title: 'Docs',
        actionType: 'link',
        link: AddOnDocUrls[AddOns.API],
        actionIsDefault: false,
      },
    ],
  },
  [AddOns.SPONSOR]: {
    title: AddOnLabels[AddOns.SPONSOR],
    icon: <IconSponsor size="2x" fixedWidth color={RacemapColors.PaleBlue} />,
    subtitle: 'Advertising space in tracking map & in leaderboard',
    actions: [
      {
        icon: <IconGear />,
        title: 'Setup',
        actionType: 'link',
        link: `../${AddOns.SPONSOR}`,
        actionIsDefault: true,
      },
      {
        icon: <IconDocs />,
        title: 'Docs',
        actionType: 'link',
        link: AddOnDocUrls[AddOns.SPONSOR],
        actionIsDefault: false,
      },
    ],
  },
  [AddOns.TIMING]: {
    title: AddOnLabels[AddOns.TIMING],
    icon: <IconLeaderboard size="2x" fixedWidth color={RacemapColors.PaleBlue} />,
    subtitle: 'Live rank & results with location data & geofences',
    actions: [
      {
        icon: <IconGear />,
        title: 'Setup',
        actionType: 'link',
        link: '../leaderboard',
        actionIsDefault: true,
      },
      {
        icon: <IconDocs />,
        title: 'Docs',
        actionType: 'link',
        link: AddOnDocUrls[AddOns.TIMING],
        actionIsDefault: false,
      },
    ],
  },
  [AddOns.KEYS]: {
    title: AddOnLabels[AddOns.KEYS],
    icon: <IconKey size="2x" fixedWidth color={RacemapColors.PaleBlue} />,
    subtitle: 'Seamless sign-up for live tracking',
    actions: [
      {
        icon: <IconGear />,
        title: 'Setup',
        actionType: 'link',
        link: `../${AddOns.KEYS}`,
        actionIsDefault: true,
      },
      {
        icon: <IconDocs />,
        title: 'Docs',
        actionType: 'link',
        link: AddOnDocUrls[AddOns.KEYS],
        actionIsDefault: false,
      },
    ],
  },
  [AddOns.TRACKER_RENTAL]: {
    title: AddOnLabels[AddOns.TRACKER_RENTAL],
    icon: <IconMobile size="2x" fixedWidth color={RacemapColors.PaleBlue} />,
    subtitle: 'Rent trackers for your event',
    actions: [
      {
        icon: <IconGear />,
        title: 'Rent',
        actionType: 'link',
        link: RentGPSTrackerFormUrl,
        actionIsDefault: true,
      },
      {
        icon: <IconInfo />,
        title: 'Info',
        actionType: 'link',
        link: RentGPSTrackerInfoUrl,
        actionIsDefault: false,
      },
    ],
  },
};

const CardAddOn = styled.div`
  display: grid;
  grid-template-columns: 50px 1fr 1fr;
  width: 100%;
  align-items: center;
  border: 1px solid ${RacemapColors.LightGray};
  border-radius: 5px;
  padding: 10px;
  cursor: pointer;

  &:hover {
    background-color: ${RacemapColors.LightLightGray};
    border-color: ${RacemapColors.PaleBlue};
  }

  .title-and-subtitle {
    min-width: 0;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 50px;
  height: 50px;
  filter: drop-shadow(0 2px 0 rgba(0, 50, 65, 0.2));
`;
